import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import HubIcon from "@mui/icons-material/Hub";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncIcon from "@mui/icons-material/Sync";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import {
  getMarketingConnection,
  getMarketingSyncLogs,
  saveMarketingConnection,
  syncMarketingConnection,
  testMarketingConnection,
} from "../../../../services/crm";
import { AuthContext } from "../../../../helpers/AuthContext";
import { formatDateTimeInTimeZone } from "../../../../utils/timezone";

const defaultForm = {
  displayName: "Insightly",
  isEnabled: false,
  authType: "api_key",
  baseUrl: "https://api.na1.insightly.com/v3.1",
  apiKey: "",
  hasSecret: false,
  maskedSecret: "",
  status: "not_configured",
  lastTestStatus: null,
  lastTestMessage: "",
  lastTestAt: null,
  lastSyncAt: null,
  syncIntervalMinutes: 0,
  nextAllowedSyncAt: null,
  configJson: {
    defaultImportObjects: ["leads", "contacts", "organizations"],
    pipelineVisibility: {
      leads: true,
      contacts: false,
      organizations: false,
    },
    syncMode: "full",
    selectedSyncRules: [],
    fieldMappings: {},
    syncLimiter: {
      manualCooldownMinutes: 0,
      allowForceSync: true,
    },
  },
};

const objectTypeOptions = [
  { value: "leads", label: "Leads" },
  { value: "contacts", label: "Contacts" },
  { value: "organisations", label: "Organisations" },
  { value: "opportunities", label: "Opportunities" },
];

const CUSTOM_FIELD_PRESET = "CUSTOM";

const fieldPresetOptionsByObject = {
  leads: [
    { value: "LEAD_STATUS_ID", label: "Lead Status", fieldName: "LEAD_STATUS_ID" },
    { value: "LEAD_SOURCE_ID", label: "Lead Source", fieldName: "LEAD_SOURCE_ID" },
    { value: "LEAD_RATING", label: "Lead Rating", fieldName: "LEAD_RATING" },
    { value: "OWNER_USER_ID", label: "Owner User", fieldName: "OWNER_USER_ID" },
    { value: "RESPONSIBLE_USER_ID", label: "Responsible User", fieldName: "RESPONSIBLE_USER_ID" },
    { value: "CONVERTED", label: "Converted", fieldName: "CONVERTED" },
    { value: CUSTOM_FIELD_PRESET, label: "Custom Field", fieldName: "" },
  ],
  contacts: [
    { value: "EMAIL_ADDRESS", label: "Email", fieldName: "EMAIL_ADDRESS" },
    { value: "FIRST_NAME", label: "First Name", fieldName: "FIRST_NAME" },
    { value: "LAST_NAME", label: "Last Name", fieldName: "LAST_NAME" },
    { value: "OWNER_USER_ID", label: "Owner User", fieldName: "OWNER_USER_ID" },
    { value: "ORGANISATION_ID", label: "Organisation ID", fieldName: "ORGANISATION_ID" },
    { value: CUSTOM_FIELD_PRESET, label: "Custom Field", fieldName: "" },
  ],
  organisations: [
    { value: "ORGANISATION_NAME", label: "Organisation Name", fieldName: "ORGANISATION_NAME" },
    { value: "OWNER_USER_ID", label: "Owner User", fieldName: "OWNER_USER_ID" },
    { value: "PHONE", label: "Phone", fieldName: "PHONE" },
    { value: "WEBSITE", label: "Website", fieldName: "WEBSITE" },
    { value: CUSTOM_FIELD_PRESET, label: "Custom Field", fieldName: "" },
  ],
  opportunities: [
    { value: "PROBABILITY", label: "Probability", fieldName: "PROBABILITY" },
    { value: "OPPORTUNITY_STATE", label: "Opportunity State", fieldName: "OPPORTUNITY_STATE" },
    { value: "OWNER_USER_ID", label: "Owner User", fieldName: "OWNER_USER_ID" },
    { value: "ORGANISATION_ID", label: "Organisation ID", fieldName: "ORGANISATION_ID" },
    { value: CUSTOM_FIELD_PRESET, label: "Custom Field", fieldName: "" },
  ],
};

fieldPresetOptionsByObject.organizations = fieldPresetOptionsByObject.organisations;

function normalizeObjectType(value) {
  const normalized = String(value || "leads").trim().toLowerCase();
  if (["organizations", "organization", "organisation"].includes(normalized)) return "organisations";
  if (["lead", "leads"].includes(normalized)) return "leads";
  if (["contact", "contacts"].includes(normalized)) return "contacts";
  if (["opportunity", "opportunities"].includes(normalized)) return "opportunities";
  return normalized || "leads";
}

function normalizeImportObject(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (["organization", "organizations", "organisation", "organisations"].includes(normalized)) {
    return "organizations";
  }
  if (["lead", "leads"].includes(normalized)) return "leads";
  if (["contact", "contacts"].includes(normalized)) return "contacts";
  return normalized;
}

function normalizeImportObjectsForForm(configJson = {}) {
  const configured = Array.isArray(configJson?.defaultImportObjects)
    ? configJson.defaultImportObjects
    : defaultForm.configJson.defaultImportObjects;
  return Array.from(new Set(configured.map(normalizeImportObject).filter(Boolean)));
}

function getPresetOptionsForObject(objectType) {
  return fieldPresetOptionsByObject[normalizeObjectType(objectType)] || fieldPresetOptionsByObject.leads;
}

function getPresetForRule(objectType, rule = {}) {
  const fieldPresetRaw = String(rule.fieldPreset || "").trim();
  const fieldPreset = fieldPresetRaw.toLowerCase() === "custom" ? CUSTOM_FIELD_PRESET : fieldPresetRaw;
  const fieldName = String(rule.fieldName || "").trim();
  const options = getPresetOptionsForObject(objectType);
  const directMatch = options.find((option) => option.value === fieldPreset);
  if (directMatch) return directMatch;
  if (fieldPreset && fieldPreset !== CUSTOM_FIELD_PRESET) {
    return options.find((option) => option.value !== CUSTOM_FIELD_PRESET) || options[0];
  }
  const fieldMatch = options.find((option) => option.fieldName && option.fieldName === fieldName);
  if (fieldMatch) return fieldMatch;
  return options.find((option) => option.value === CUSTOM_FIELD_PRESET) || options[0];
}

function buildRuleForObjectType(objectType, existingRule = {}) {
  const normalizedObjectType = normalizeObjectType(objectType);
  const preset = getPresetForRule(normalizedObjectType, existingRule);
  const isCustom = preset?.value === CUSTOM_FIELD_PRESET;

  return {
    ...existingRule,
    objectType: normalizedObjectType,
    fieldPreset: preset?.value || "",
    fieldName: isCustom ? String(existingRule.fieldName || "").trim() : preset?.fieldName || "",
  };
}

function normalizeRuleForForm(rule = {}) {
  const objectType = normalizeObjectType(rule.objectType || "leads");
  const normalizedRule = buildRuleForObjectType(objectType, rule);

  return {
    objectType: normalizedRule.objectType,
    fieldPreset: normalizedRule.fieldPreset,
    fieldName: normalizedRule.fieldName,
    fieldValue:
      rule.fieldValue === undefined || rule.fieldValue === null
        ? ""
        : String(rule.fieldValue),
    enabled: rule.enabled === true,
  };
}

function normalizeConfigForForm(configJson = {}) {
  return {
    ...defaultForm.configJson,
    ...(configJson || {}),
    defaultImportObjects: normalizeImportObjectsForForm(configJson),
    pipelineVisibility: {
      ...defaultForm.configJson.pipelineVisibility,
      ...(configJson?.pipelineVisibility || {}),
    },
    syncMode: configJson?.syncMode === "selected" ? "selected" : "full",
    selectedSyncRules: Array.isArray(configJson?.selectedSyncRules)
      ? configJson.selectedSyncRules.map(normalizeRuleForForm)
      : [],
    syncLimiter: {
      ...defaultForm.configJson.syncLimiter,
      ...(configJson?.syncLimiter || {}),
    },
  };
}

function getRuleValidation(rule = {}) {
  if (rule.enabled !== true) return null;
  const objectType = normalizeObjectType(rule.objectType);
  const fieldPreset = String(rule.fieldPreset || "").trim();
  const fieldName = String(rule.fieldName || "").trim();
  const fieldValue =
    rule.fieldValue === undefined || rule.fieldValue === null
      ? ""
      : String(rule.fieldValue).trim();

  if (!objectType) return "Object Type is required.";
  if (!fieldName) return "Field Name is required.";
  if (!fieldValue) return "Field Value is required.";

  const options = getPresetOptionsForObject(objectType);
  const preset = options.find((option) => option.value === fieldPreset);
  if (fieldPreset && fieldPreset !== CUSTOM_FIELD_PRESET && !preset) {
    return `Preset ${fieldPreset} is not valid for ${objectType}.`;
  }

  return null;
}

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.error || fallback;
}

function statusChipColor(status) {
  switch (String(status || "").toLowerCase()) {
    case "success":
    case "connected":
      return "success";
    case "running":
      return "info";
    case "blocked":
      return "warning";
    case "failed":
    case "error":
      return "error";
    default:
      return "default";
  }
}

export default function MarketingConnectionsPanel({ instanceUniqueId }) {
  const { activeInstanceTimezone } = useContext(AuthContext);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [forceSync, setForceSync] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]);
  const [notice, setNotice] = useState(null);
  const formatInstanceDateTime = (value) =>
    formatDateTimeInTimeZone(value, { timeZone: activeInstanceTimezone }) || "Not available";

  const setField = (key) => (event) => {
    setForm((current) => ({
      ...current,
      [key]: event.target.value,
    }));
  };

  const setLimiterField = (key, value) => {
    setForm((current) => ({
      ...current,
      configJson: {
        ...current.configJson,
        syncLimiter: {
          ...current.configJson.syncLimiter,
          [key]: value,
        },
      },
    }));
  };

  const setConfigField = (key, value) => {
    setForm((current) => ({
      ...current,
      configJson: {
        ...current.configJson,
        [key]: value,
      },
    }));
  };

  const setPipelineVisibility = (key, value) => {
    setForm((current) => ({
      ...current,
      configJson: {
        ...current.configJson,
        pipelineVisibility: {
          ...current.configJson.pipelineVisibility,
          [key]: value,
        },
      },
    }));
  };

  const setSyncScope = (key, value) => {
    setForm((current) => {
      const currentObjects = normalizeImportObjectsForForm(current.configJson);
      const nextObjects = value
        ? Array.from(new Set([...currentObjects, key]))
        : currentObjects.filter((objectType) => objectType !== key);
      return {
        ...current,
        configJson: {
          ...current.configJson,
          defaultImportObjects: nextObjects,
        },
      };
    });
  };

  const updateSelectedRule = (index, patch) => {
    setForm((current) => {
      const currentRules = Array.isArray(current.configJson?.selectedSyncRules)
        ? current.configJson.selectedSyncRules
        : [];
      const nextRules = currentRules.map((rule, ruleIndex) =>
        ruleIndex === index ? { ...rule, ...patch } : rule
      );

      return {
        ...current,
        configJson: {
          ...current.configJson,
          selectedSyncRules: nextRules,
        },
      };
    });
  };

  const addSelectedRule = () => {
    setForm((current) => ({
      ...current,
      configJson: {
        ...current.configJson,
        selectedSyncRules: [
          ...(Array.isArray(current.configJson?.selectedSyncRules)
            ? current.configJson.selectedSyncRules
            : []),
          {
            objectType: "leads",
            fieldPreset: "LEAD_STATUS_ID",
            fieldName: "LEAD_STATUS_ID",
            fieldValue: "",
            enabled: true,
          },
        ],
      },
    }));
  };

  const deleteSelectedRule = (index) => {
    setForm((current) => ({
      ...current,
      configJson: {
        ...current.configJson,
        selectedSyncRules: (current.configJson?.selectedSyncRules || []).filter(
          (_rule, ruleIndex) => ruleIndex !== index
        ),
      },
    }));
  };

  const handleRulePresetChange = (index, value) => {
    setForm((current) => {
      const currentRules = Array.isArray(current.configJson?.selectedSyncRules)
        ? current.configJson.selectedSyncRules
        : [];
      const nextRules = currentRules.map((rule, ruleIndex) => {
        if (ruleIndex !== index) return rule;
        const preset = getPresetOptionsForObject(rule.objectType).find(
          (option) => option.value === value
        );
        const isCustom = value === CUSTOM_FIELD_PRESET;
        return {
          ...rule,
          fieldPreset: value,
          fieldName: isCustom ? "" : preset?.fieldName || "",
        };
      });

      return {
        ...current,
        configJson: {
          ...current.configJson,
          selectedSyncRules: nextRules,
        },
      };
    });
  };

  const handleRuleObjectTypeChange = (index, value) => {
    setForm((current) => {
      const currentRules = Array.isArray(current.configJson?.selectedSyncRules)
        ? current.configJson.selectedSyncRules
        : [];
      const nextRules = currentRules.map((rule, ruleIndex) =>
        ruleIndex === index ? buildRuleForObjectType(value, rule) : rule
      );

      return {
        ...current,
        configJson: {
          ...current.configJson,
          selectedSyncRules: nextRules,
        },
      };
    });
  };

  const loadConnection = async () => {
    if (!instanceUniqueId) return;
    setLoading(true);
    try {
      const response = await getMarketingConnection(instanceUniqueId, "insightly");
      const result = response.data?.result || {};
      setForm({
        ...defaultForm,
        ...result,
        apiKey: "",
        configJson: normalizeConfigForForm(result.configJson || {}),
      });
      setNotice(null);
    } catch (error) {
      if (error?.response?.status === 404) {
        setForm(defaultForm);
        setNotice(null);
      } else {
        setNotice({
          severity: "error",
          message: getErrorMessage(error, "Failed to load Marketing Connections settings."),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    if (!instanceUniqueId) return;
    try {
      const response = await getMarketingSyncLogs(instanceUniqueId, "insightly", { rpp: 10 });
      setSyncLogs(Array.isArray(response.data?.results) ? response.data.results : []);
    } catch (error) {
      setNotice({
        severity: "error",
        message: getErrorMessage(error, "Failed to load sync logs."),
      });
    }
  };

  const refreshAll = async () => {
    await Promise.all([loadConnection(), loadLogs()]);
  };

  const saveConnection = async () => {
    setSaving(true);
    try {
      const selectedSyncRules = Array.isArray(form.configJson?.selectedSyncRules)
        ? form.configJson.selectedSyncRules.map((rule) => ({
            ...rule,
            objectType: normalizeObjectType(rule.objectType),
            fieldPreset:
              String(rule.fieldPreset || "").trim().toLowerCase() === "custom"
                ? CUSTOM_FIELD_PRESET
                : String(rule.fieldPreset || "").trim(),
            fieldName: String(rule.fieldName || "").trim(),
            fieldValue:
              rule.fieldValue === undefined || rule.fieldValue === null
                ? ""
                : String(rule.fieldValue).trim(),
            enabled: rule.enabled === true,
          }))
        : [];

      const response = await saveMarketingConnection(instanceUniqueId, "insightly", {
        displayName: form.displayName,
        isEnabled: form.isEnabled,
        authType: form.authType,
        baseUrl: form.baseUrl,
        apiKey: form.apiKey,
        syncIntervalMinutes: Number(form.syncIntervalMinutes) || 0,
        configJson: {
          ...form.configJson,
          defaultImportObjects: normalizeImportObjectsForForm(form.configJson),
          syncMode: form.configJson?.syncMode === "selected" ? "selected" : "full",
          selectedSyncRules,
          syncLimiter: {
            ...form.configJson.syncLimiter,
            manualCooldownMinutes:
              Number(form.configJson?.syncLimiter?.manualCooldownMinutes) || 0,
            allowForceSync: form.configJson?.syncLimiter?.allowForceSync === true,
          },
        },
      });

      const result = response.data?.result || {};
      setForm((current) => ({
        ...current,
        ...result,
        apiKey: "",
        configJson: normalizeConfigForForm(result.configJson || {}),
      }));
      setNotice({
        severity: "success",
        message: "Marketing connection settings saved.",
      });
      await loadLogs();
    } catch (error) {
      setNotice({
        severity: "error",
        message: getErrorMessage(error, "Failed to save Marketing Connections settings."),
      });
    } finally {
      setSaving(false);
    }
  };

  const runConnectionTest = async () => {
    setTesting(true);
    try {
      const response = await testMarketingConnection(instanceUniqueId, "insightly");
      setNotice({
        severity: "success",
        message: response.data?.message || "Connection test succeeded.",
      });
      await refreshAll();
    } catch (error) {
      setNotice({
        severity: "error",
        message: getErrorMessage(error, "Connection test failed."),
      });
      await refreshAll();
    } finally {
      setTesting(false);
    }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const response = await syncMarketingConnection(instanceUniqueId, "insightly", {
        forceSync,
      });
      setNotice({
        severity: "success",
        message: response.data?.message || "Sync completed successfully.",
      });
      await refreshAll();
    } catch (error) {
      const cooldownCode = error?.response?.data?.code;
      setNotice({
        severity: cooldownCode === "SYNC_COOLDOWN_ACTIVE" ? "warning" : "error",
        message: getErrorMessage(error, "Sync failed."),
      });
      await refreshAll();
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  const selectedRuleErrors =
    form.configJson?.syncMode === "selected"
      ? (form.configJson?.selectedSyncRules || []).map(getRuleValidation)
      : [];
  const hasSelectedRuleErrors = selectedRuleErrors.some(Boolean);
  const saveDisabled = loading || saving || testing || syncing || hasSelectedRuleErrors;

  return (
    <Card elevation={3} sx={{ borderRadius: 2 }}>
      <CardHeader
        avatar={<HubIcon color="primary" />}
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="h6">Marketing Connections</Typography>
            <Chip size="small" label="Insightly" color="primary" variant="outlined" />
            <Chip
              size="small"
              label={form.status || "not_configured"}
              color={statusChipColor(form.status)}
            />
            {loading ? <CircularProgress size={16} /> : null}
          </Box>
        }
        subheader="Configure provider-backed marketing prospect sync for this instance."
        action={
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={refreshAll}
              disabled={loading || saving || testing || syncing}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              onClick={runConnectionTest}
              disabled={loading || saving || testing || syncing}
            >
              {testing ? "Testing..." : "Test Connection"}
            </Button>
            <Button
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={runSync}
              disabled={loading || saving || testing || syncing}
            >
              {syncing ? "Running..." : "Run Sync"}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={saveConnection}
              disabled={saveDisabled}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </Box>
        }
      />
      <Divider />
      <CardContent>
        {notice ? (
          <Alert severity={notice.severity} sx={{ mb: 2 }}>
            {notice.message}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          <TextField
            label="Display Name"
            value={form.displayName || ""}
            onChange={setField("displayName")}
            fullWidth
          />
          <TextField
            label="Base URL"
            value={form.baseUrl || ""}
            onChange={setField("baseUrl")}
            fullWidth
          />
          <TextField
            label="API Key"
            type="password"
            value={form.apiKey || ""}
            onChange={setField("apiKey")}
            fullWidth
            placeholder={form.hasSecret ? form.maskedSecret || "Configured" : "Enter API key"}
            helperText={
              form.hasSecret
                ? `Saved key: ${form.maskedSecret}. Leave blank to preserve the stored key.`
                : "Enter the Insightly API key for this instance."
            }
          />
          <TextField
            label="Sync Interval Minutes"
            type="number"
            value={form.syncIntervalMinutes ?? 0}
            onChange={setField("syncIntervalMinutes")}
            fullWidth
            inputProps={{ min: 0 }}
            helperText="Stored for future scheduled sync support. 0 means no scheduled interval."
          />
          <TextField
            label="Manual Cooldown Minutes"
            type="number"
            value={form.configJson?.syncLimiter?.manualCooldownMinutes ?? 0}
            onChange={(event) =>
              setLimiterField("manualCooldownMinutes", Number(event.target.value) || 0)
            }
            fullWidth
            inputProps={{ min: 0 }}
            helperText="Blocks standard manual sync until the cooldown window passes."
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 1,
              px: 1,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={form.isEnabled === true}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      isEnabled: event.target.checked,
                    }))
                  }
                />
              }
              label="Enable Insightly connection"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.configJson?.syncLimiter?.allowForceSync === true}
                  onChange={(event) =>
                    setLimiterField("allowForceSync", event.target.checked)
                  }
                />
              }
              label="Allow force sync during cooldown"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={forceSync}
                  onChange={(event) => setForceSync(event.target.checked)}
                />
              }
              label="Use force sync for the next manual sync run"
            />
          </Box>
        </Box>

        <Box
          sx={{
            mt: 3,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Sync from Insightly
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Choose which Insightly record types are fetched and synchronized. Existing records are not deleted when a type is disabled.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {[
              ["leads", "Leads"],
              ["contacts", "Contacts"],
              ["organizations", "Organizations"],
            ].map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    checked={normalizeImportObjectsForForm(form.configJson).includes(key)}
                    onChange={(event) => setSyncScope(key, event.target.checked)}
                  />
                }
                label={label}
              />
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            mt: 3,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Pipeline Visibility
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Choose which Insightly record types appear in the CRM pipeline. Hidden records remain synchronized and stored.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {[
              ["leads", "Leads"],
              ["contacts", "Contacts"],
              ["organizations", "Organizations"],
            ].map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    checked={form.configJson?.pipelineVisibility?.[key] === true}
                    onChange={(event) => setPipelineVisibility(key, event.target.checked)}
                  />
                }
                label={label}
              />
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            mt: 3,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Last Test
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, mb: 1 }}>
                <Chip
                  size="small"
                  label={form.lastTestStatus || "not_run"}
                  color={statusChipColor(form.lastTestStatus)}
                />
              </Box>
              <Typography variant="body2">{form.lastTestMessage || "No test has been run yet."}</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {formatInstanceDateTime(form.lastTestAt)}
              </Typography>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Last Sync
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {formatInstanceDateTime(form.lastSyncAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Next allowed manual sync: {formatInstanceDateTime(form.nextAllowedSyncAt)}
              </Typography>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Secret Status
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                <ShieldOutlinedIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {form.hasSecret
                    ? `Stored server-side as ${form.maskedSecret}`
                    : "No API key saved for this instance."}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box
          sx={{
            mt: 3,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Sync Scope
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose whether Insightly pulls all configured objects or only records matching Search rules.
              </Typography>
            </Box>
            <TextField
              select
              size="small"
              label="Sync Mode"
              value={form.configJson?.syncMode === "selected" ? "selected" : "full"}
              onChange={(event) => setConfigField("syncMode", event.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="full">Full Sync</MenuItem>
              <MenuItem value="selected">Selected Sync</MenuItem>
            </TextField>
          </Box>

          {form.configJson?.syncMode === "selected" ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Presets are limited by Insightly object type. Use Custom Field only when you know the exact Insightly FIELD_NAME.
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addSelectedRule}
                >
                  Add Rule
                </Button>
              </Box>

              {(form.configJson?.selectedSyncRules || []).length ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {(form.configJson?.selectedSyncRules || []).map((rule, index) => {
                    const presetOptions = getPresetOptionsForObject(rule.objectType);
                    const isCustom = rule.fieldPreset === CUSTOM_FIELD_PRESET;
                    const ruleError = selectedRuleErrors[index];
                    return (
                      <Box
                        key={`selected-sync-rule-${index}`}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "1.2fr 1.2fr 1.4fr 1.2fr auto auto",
                          },
                          gap: 1,
                          alignItems: "center",
                          p: 1.25,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          backgroundColor: "background.default",
                        }}
                      >
                        <TextField
                          select
                          size="small"
                          label="Object Type"
                          value={rule.objectType || "leads"}
                          onChange={(event) =>
                            handleRuleObjectTypeChange(index, event.target.value)
                          }
                          fullWidth
                        >
                          {objectTypeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          select
                          size="small"
                          label="Field Preset"
                          value={rule.fieldPreset || CUSTOM_FIELD_PRESET}
                          onChange={(event) => handleRulePresetChange(index, event.target.value)}
                          fullWidth
                        >
                          {presetOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          size="small"
                          label="Field Name"
                          value={rule.fieldName || ""}
                          onChange={(event) =>
                            updateSelectedRule(index, { fieldName: event.target.value })
                          }
                          InputProps={{ readOnly: !isCustom }}
                          error={Boolean(ruleError && String(ruleError).includes("Field Name"))}
                          fullWidth
                          helperText={
                            isCustom
                              ? "Custom field names must match Insightly FIELD_NAME exactly."
                              : "Set by preset"
                          }
                        />

                        <TextField
                          size="small"
                          label="Field Value"
                          value={rule.fieldValue || ""}
                          onChange={(event) =>
                            updateSelectedRule(index, { fieldValue: event.target.value })
                          }
                          error={Boolean(ruleError && String(ruleError).includes("Field Value"))}
                          helperText={ruleError && String(ruleError).includes("Field Value") ? ruleError : ""}
                          fullWidth
                        />

                        <FormControlLabel
                          control={
                            <Switch
                              checked={rule.enabled === true}
                              onChange={(event) =>
                                updateSelectedRule(index, { enabled: event.target.checked })
                              }
                            />
                          }
                          label="Enabled"
                          sx={{ m: 0, whiteSpace: "nowrap" }}
                        />

                        <IconButton
                          aria-label="Delete selected sync rule"
                          color="error"
                          onClick={() => deleteSelectedRule(index)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>

                        {ruleError && !String(ruleError).includes("Field Value") ? (
                          <Alert
                            severity="warning"
                            sx={{
                              gridColumn: { xs: "1", md: "1 / -1" },
                              py: 0.5,
                            }}
                          >
                            {ruleError}
                          </Alert>
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Selected Sync requires at least one enabled rule before Run Sync will call Insightly.
                </Alert>
              )}
            </Box>
          ) : (
            <Alert severity="info">
              Pulls all records from the selected Insightly objects.
            </Alert>
          )}
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Recent Sync Logs
          </Typography>
          <Box sx={{ overflowX: "auto", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Trigger</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Finished</TableCell>
                  <TableCell align="right">Processed</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {syncLogs.length ? (
                  syncLogs.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status || "unknown"}
                          color={statusChipColor(row.status)}
                        />
                      </TableCell>
                      <TableCell>{row.triggerType || row.syncType || "-"}</TableCell>
                      <TableCell>{formatInstanceDateTime(row.startedAt)}</TableCell>
                      <TableCell>{formatInstanceDateTime(row.finishedAt)}</TableCell>
                      <TableCell align="right">{row.recordsRead || 0}</TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2" noWrap>
                          {row.message || "-"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No sync logs available yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
